import type { Layout, LayoutItem } from "react-grid-layout/legacy";
import type { ComponentSpec } from "./Processing/types";
import { getWidgetDef } from "./widgetRegistry";

/**
 * Known widget type IDs, derived from the `ComponentSpec` discriminated union so
 * this stays in lockstep with the registry — no hand-maintained list to drift.
 */
export type GridWidgetType = ComponentSpec["type"];

/** A single widget instance: id, type, and optional metadata / spec. */
export interface GridWidgetInstance {
  id: string;
  type: GridWidgetType;
  meta?: Record<string, unknown>;
  /** ComponentSpec built by the AddWidgetModal configurator (chart, table, etc.). */
  spec?: ComponentSpec;
}

/** Full grid state: layout array and items by id. Serialization-friendly. */
export interface GridLayoutState {
  layout: Layout;
  items: Record<string, GridWidgetInstance>;
}

/** Request to add a new widget to the grid. */
export interface AddWidgetRequest {
  type: GridWidgetType;
  initialMeta?: Record<string, unknown>;
  /** Pre-built ComponentSpec from the widget configurator. */
  initialSpec?: ComponentSpec;
}

export { DEFAULT_GRID_COLS, DEFAULT_ROW_HEIGHT, DEFAULT_GRID_MARGIN } from "./gridConstants";

/** Compact type for react-grid-layout. */
export type GridCompactType = "vertical" | "horizontal" | null;

/**
 * Returns default layout dimensions for a widget type.
 * Looks up the widget definition registry; unknown types get a generic cell.
 */
export function getDefaultLayoutForType(
  type: GridWidgetType,
  id: string,
  x: number,
  y: number
): LayoutItem {
  const def = getWidgetDef(type);
  const base = def?.defaultLayout ?? { w: 6, h: 3, minW: 3, minH: 2 };
  return { i: id, x, y, ...base };
}
